import React, { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import Template from "../../components/Template";
import api from "../../services/api";
import ConfirmationModal from "../../components/ConfirmationModal";
import SuccessModal from "../../components/SuccessModal";
import { formatDateWithHour, formatDateWithoutHour } from "../../utils/date";
import { maskPhone, maskCurrency, formatCurrencyFromDb } from "../../utils/masks";
import { parseCurrencyToFloat } from "../../utils/utils";
import config from "../../config";
import FormLabel from "../../components/FormLabel";
import usePermission from "../../hooks/usePermission";

const CadastrarContrato = ({ contratoId }) => {
    const navigate = useNavigate();
    const isEditMode = Boolean(contratoId);
    const podeDeletar = usePermission("DELETAR_CONTRATO");

    // Estados para modais de confirmação/sucesso
    const [showConfirmDialog, setShowConfirmDialog] = useState(false);
    const [showSuccessDialog, setShowSuccessDialog] = useState(false);
    const [showConfirmDeleteRegister, setShowConfirmDeleteRegister] = useState(false);
    const [showConfirmRemoveArchiveDialog, setShowConfirmRemoveArchiveDialog] = useState(false);

    // Estados para listas (ex: categorias, status, etc.)
    const [categorias, setCategorias] = useState([]);
    const [statusList, setStatusList] = useState([]);
    const [prazos, setPrazos] = useState([]);
    const [statusMedicoesList, setStatusMedicoesList] = useState([]);
    const [orgaosEmissores, setOrgaosEmissores] = useState([]);
    const [modalidades, setModalidades] = useState([]);
    const [tiposDocumentos, setTiposDocumentos] = useState([]);

    // Arrays dinâmicos para medições e anexos
    const [medicoes, setMedicoes] = useState([]);
    const [medicoesRemovidas, setMedicoesRemovidas] = useState([]);
    const [anexosSelecionados, setAnexosSelecionados] = useState([]);
    const [anexosRemovidos, setAnexosRemovidos] = useState([]);
    const [anexoFile, setAnexoFile] = useState(null);

    const [showDialog, setShowDialog] = useState(false);
    const [selectedPdf, setSelectedPdf] = useState("");

    // Estado principal do formulário
    const [formData, setFormData] = useState({
        numeroContrato: "",
        dataPublicacao: "",
        objetoContrato: "",
        valorContrato: "",
        valorDesconto: "",
        processoAdministrativo: "",
        prazoExecucao: "",
        dataInicio: "",
        dataTermino: "",
        bdiPercentual: "",
        descontoPercentual: "",
        numeroART: "",
        responsavelART: "",
        saldoMedir: "",
        // Medições
        // Os campos das medições serão gerenciados separadamente na lista 'medicoes'
        // Anexos
        tipoDocumentoAnexo: "",
        statusId: "",
        arquivo: null, // Para upload de arquivos
    });

    // Carregar dados do contrato (edição)
    useEffect(() => {
        const fetchData = async () => {
            try {
                const [categoriasRes, statusRes, modalidadesRes, orgaosEmissoresRes, tiposDocumentosRes, statusMedicoesRes, prazosExecucaoRes] = await Promise.all([
                    api.get("/categorias"),
                    api.get("/statuses/byModule/nome/contratos"),
                    api.get("/modalidades"),
                    api.get("/orgaosemissores"),
                    api.get("/tiposdocumentos"),
                    api.get("/statuses/byModule/nome/medicoes"),
                    api.get("/prazosexecucao"),
                ]);
                setCategorias(categoriasRes.data);
                setStatusList(statusRes.data);
                setModalidades(modalidadesRes.data);
                setOrgaosEmissores(orgaosEmissoresRes.data);
                setTiposDocumentos(tiposDocumentosRes.data);
                setStatusMedicoesList(statusMedicoesRes.data);
                setPrazos(prazosExecucaoRes.data);
            } catch (error) {
                console.error("Erro ao carregar dados:", error);
            }
        };

        fetchData();

        if (isEditMode) {
            const fetchContrato = async () => {
                try {
                    const response = await api.get(`/contratos/${contratoId}`);
                    const data = response.data;
                    const acumulado = (data.medicoes || []).reduce((acc, medicao) => acc + (medicao.valorMedido || 0), 0);

                    const saldoMedir = data.valorContrato - acumulado;
                    setFormData({
                        numeroContrato: data.numeroContrato || "",
                        dataPublicacao: data.dataPublicacao ? data.dataPublicacao.split("T")[0] : "",
                        objetoContrato: data.objetoContrato || "",
                        valorContrato: formatCurrencyFromDb(data.valorContrato) || "",
                        processoAdministrativo: data.processoAdministrativo || "",
                        prazoExecucao: data.prazoExecucao?.id || "",
                        dataInicio: data.dataInicio ? data.dataInicio.split("T")[0] : "",
                        dataTermino: data.dataTermino ? data.dataTermino.split("T")[0] : "",
                        bdiPercentual: data.bdiPercentual || "",
                        descontoPercentual: data.descontoPercentual || "",
                        numeroART: data.numeroART || "",
                        responsavelART: data.responsavelART || "",
                        saldoMedir: formatCurrencyFromDb(saldoMedir),
                        acumulado: formatCurrencyFromDb(acumulado),
                        statusId: data.status?.id,
                        // Anexos
                        tipoDocumentoAnexo: "",
                        arquivo: null,
                    });
                    setMedicoes(data.medicoes || []);
                    setAnexosSelecionados(data.anexos || []);
                    setAnexosRemovidos([]);
                    setMedicoesRemovidas([]);
                } catch (error) {
                    console.error("Erro ao carregar contrato:", error);
                    alert("Erro ao carregar o contrato. Tente novamente.");
                }
            };
            fetchContrato();
        }
    }, [contratoId, isEditMode]);

    // Manipulador genérico para inputs e selects
    const handleChange = (e) => {
        const { name, value, files } = e.target;
        if (name === "arquivo") {
            setFormData((prevData) => ({
                ...prevData,
                [name]: files[0],
            }));
        } else {
            setFormData((prevData) => ({
                ...prevData,
                [name]: value,
            }));
        }
    };

    // Handler genérico para inputs e selects
    const handleInputChange = (e) => {
        const { name, value } = e.target;
        setFormData((prev) => ({ ...prev, [name]: value }));
    };

    // Manipulador específico para campos de moeda
    const handleCurrencyChange = (fieldName) => (e) => {
        const originalValue = e.target.value;
        const masked = maskCurrency(originalValue);
        setFormData((prev) => ({ ...prev, [fieldName]: masked }));
    };

    const handleAdicionarMedicao = () => {
        const novoValorMedido = parseCurrencyToFloat(formData.valorMedido);

        const statusId = parseInt(formData.statusMedicao, 10);
        const statusEncontrado = statusList.find((s) => s.id === statusId);

        setMedicoes((prevMedicoes) => {
            const novasMedicoes = [
                ...prevMedicoes,
                {
                    id: Date.now(), // ID único local
                    valorMedido: novoValorMedido,
                    numeroNotaFiscal: formData.numeroNotaFiscal,
                    periodo: formData.periodo,
                    statusNome: statusEncontrado.nome,
                    statusMedicao: formData.statusMedicao,
                    numeroEmpenho: formData.numeroEmpenho,
                    numeroMedicao: formData.numeroMedicao,
                    dataEnvio: Date.now(),
                    isNew: true,
                },
            ];

            // Atualiza o acumulado com base nas novas medições
            const novoAcumulado = novasMedicoes.reduce((acc, medicao) => acc + (medicao.valorMedido || 0), 0);

            const saldoMedir =
                parseCurrencyToFloat(formData.valorContrato) - novoAcumulado;

            // Atualiza o estado do formulário com o novo acumulado
            setFormData((prev) => ({
                ...prev,
                acumulado: maskCurrency(novoAcumulado.toFixed(2)), // Formata o acumulado
                saldoMedir: maskCurrency(saldoMedir.toFixed(2)),
            }));

            return novasMedicoes;
        });

        // Limpa os campos do formulário
        setFormData((prev) => ({
            ...prev,
            valorMedido: '',
            numeroNotaFiscal: '',
            periodo: '',
            statusMedicao: '',
            numeroEmpenho: '',
            numeroMedicao: ''
        }));
    };

    // Manipulador para atualizar uma medição específica
    const handleMedicaoChange = (id, field, value) => {
        const updatedMedicoes = medicoes.map((medicao) => {
            if (medicao.id === id) {
                return { ...medicao, [field]: value };
            }
            return medicao;
        });
        setMedicoes(updatedMedicoes);
    };

    // Manipulador para remover uma medição específica
    const handleApagarMedicao = (id) => {
        setMedicoes((prevMedicoes) => {
            // 1) Localiza a medição
            const item = prevMedicoes.find((a) => a.id === id);
            if (!item) return prevMedicoes; // se não achar, não faz nada

            // 2) Se for uma medição nova (não persistida no backend)
            if (item.isNew || String(item.id).startsWith('temp-')) {
                const novasMedicoes = prevMedicoes.filter((a) => a.id !== id);

                // Recalcula o acumulado para medições restantes
                const novoAcumulado = novasMedicoes.reduce((acc, medicao) => acc + (medicao.valorMedido || 0), 0);
                const saldoMedir = parseCurrencyToFloat(formData.valorContrato) - novoAcumulado;

                setFormData((prev) => ({
                    ...prev,
                    acumulado: maskCurrency(novoAcumulado.toFixed(2)),
                    saldoMedir: maskCurrency(saldoMedir.toFixed(2)),
                }));



                return novasMedicoes;
            } else {
                // 3) Adiciona a medição removida em um array separado (se ainda não estiver lá)
                setMedicoesRemovidas((prevRemovidos) => {
                    const jaExiste = prevRemovidos.some((r) => r.id === item.id);
                    if (!jaExiste) {
                        return [...prevRemovidos, item];
                    }
                    return prevRemovidos;
                });

                // 4) Remove a medição do array principal
                const novasMedicoes = prevMedicoes.filter((a) => a.id !== id);

                // Recalcula o acumulado para medições restantes
                const novoAcumulado = novasMedicoes.reduce((acc, medicao) => acc + (medicao.valorMedido || 0), 0);
                const saldoMedir = parseCurrencyToFloat(formData.valorContrato) - novoAcumulado;

                setFormData((prev) => ({
                    ...prev,
                    acumulado: maskCurrency(novoAcumulado.toFixed(2)),
                    saldoMedir: maskCurrency(saldoMedir.toFixed(2)),
                }));

                return novasMedicoes;
            }
        });
    };

    // Manipulador para selecionar um arquivo de anexo
    const handleAnexoFileChange = (e) => {
        setAnexoFile(e.target.files[0]);
    };

    // Manipulador para adicionar um novo anexo
    const handleAddAnexo = () => {
        if (!formData.tipoDocumentoAnexo) {
            alert("Selecione um Tipo de Documento antes de enviar.");
            return;
        }
        if (!anexoFile) {
            alert("Selecione um arquivo antes de enviar.");
            return;
        }

        // Encontrar no array "tiposDocumentos" o objeto do tipo selecionado
        const tipoId = parseInt(formData.tipoDocumentoAnexo, 10);
        const tipoEncontrado = tiposDocumentos.find((t) => t.id === tipoId);
        if (!tipoEncontrado) {
            alert("Tipo de documento não encontrado.");
            return;
        }

        // Cria objeto com tipo, file, data de envio, etc.
        const novoAnexo = {
            id: Date.now(), // ID único local
            tipoId: tipoId,
            tipoNome: tipoEncontrado.nome,
            file: anexoFile,
            dataEnvio: new Date().toISOString(),
            isNew: true,
        };
        setAnexosSelecionados((prev) => [...prev, novoAnexo]);
        setAnexoFile(null); // Resetar o campo de arquivo
    };

    // Manipulador para remover um anexo
    /*const handleRemoveAnexo = (id) => {
        setAnexosSelecionados((prevSelecionados) => prevSelecionados.filter((anexo) => anexo.id !== id));
        // Se necessário, adicione lógica para marcar anexos removidos para exclusão no backend
    };*/

    const handleRemoveAnexo = (anexoId) => {
        setAnexosSelecionados((prevSelecionados) => {
            // 1) Localiza o anexo
            const item = prevSelecionados.find((a) => a.id === anexoId);
            if (!item) return prevSelecionados; // se não achar, não faz nada

            // 2) Se é novo (isNew === true) ou ID temporário (p. ex.: 'temp-...'),
            //    basta remover do array principal, pois não precisa excluir no backend.
            if (item.isNew || String(item.id).startsWith('temp-')) {
                return prevSelecionados.filter((a) => a.id !== anexoId);
            } else {
                // 3) Caso contrário, movemos para anexosRemovidos,
                //    MAS evitamos duplicar caso já esteja lá.
                setAnexosRemovidos((prevRemovidos) => {
                    const jaExiste = prevRemovidos.some((r) => r.id === item.id);
                    if (!jaExiste) {
                        return [...prevRemovidos, item];
                    }
                    return prevRemovidos;
                });

                // 4) E removemos do array principal
                return prevSelecionados.filter((a) => a.id !== anexoId);
            }
        });
    };

    // Manipulador de submissão do formulário
    const handleSubmit = (e) => {
        e.preventDefault();
        setShowConfirmDialog(true);
    };

    // Função para confirmar e enviar os dados para a API
    const handleConfirmSubmit = async () => {
        setShowConfirmDialog(false);

        try {
            const dataToSend = new FormData();

            // Adiciona todos os campos do formData no dataToSend
            /*for (const key in formData) {
                if (formData.hasOwnProperty(key)) {
                    if (key === "arquivo" && formData[key]) {
                        dataToSend.append(key, formData[key], formData[key].name);
                    } else {
                        dataToSend.append(key, formData[key]);
                    }
                }
            }*/
            // Adiciona explicitamente cada campo do formData no formDataToSend
            dataToSend.append("numeroContrato", formData.numeroContrato);
            dataToSend.append("dataPublicacao", formData.dataPublicacao);
            dataToSend.append("objetoContrato", formData.objetoContrato);
            dataToSend.append("valorContrato", parseCurrencyToFloat(formData.valorContrato));
            dataToSend.append("processoAdministrativo", formData.processoAdministrativo);
            dataToSend.append("prazoExecucao", formData.prazoExecucao);
            dataToSend.append("dataInicio", formData.dataInicio);
            dataToSend.append("dataTermino", formData.dataTermino);
            dataToSend.append("bdiPercentual", formData.bdiPercentual);
            dataToSend.append("descontoPercentual", formData.descontoPercentual);
            dataToSend.append("numeroART", formData.numeroART);
            dataToSend.append("responsavelART", formData.responsavelART);
            dataToSend.append("saldoMedir", parseCurrencyToFloat(formData.saldoMedir));
            dataToSend.append("statusId", formData.statusId);

            dataToSend.append("anexosRemovidos", JSON.stringify(anexosRemovidos));
            dataToSend.append("medicoesRemovidas", JSON.stringify(medicoesRemovidas));

            //dataToSend.append("medicoes", JSON.stringify(medicoes));
            anexosSelecionados.forEach((anexo, index) => {
                if (anexo.isNew && anexo.file) {
                    dataToSend.append(`anexos[${index}][tipoId]`, anexo.tipoId);
                    dataToSend.append(`anexosFiles[${index}]`, anexo.file, anexo.file.name);
                    dataToSend.append(`anexos[${index}][dataEnvio]`, anexo.dataEnvio);
                    dataToSend.append(`anexos[${index}][isNew]`, anexo.isNew);
                }
            });

            // Envia para a API
            const response = await api({
                method: isEditMode ? "PUT" : "POST",
                url: isEditMode ? `/contratos/${contratoId}` : "/contratos",
                data: dataToSend,
                headers: { "Content-Type": "multipart/form-data" },
            });

            if (response.status === 200 || response.status === 201) {
                setShowSuccessDialog(true);
                setTimeout(() => {
                    setShowSuccessDialog(false);
                    if (isEditMode) {
                        window.location.href = `/contratos/editar/${contratoId}`;
                    } else {
                        window.location.href = `/contratos`;
                    }
                }, 3000);
            } else {
                throw new Error("Erro ao salvar contrato");
            }
        } catch (error) {
            console.error(`Erro ao ${isEditMode ? "atualizar" : "cadastrar"} contrato:`, error);
            alert("Ocorreu um erro. Verifique os dados e tente novamente.");
        }
    };

    // Cancelar modal de confirmação
    const handleCancelSubmit = () => {
        setShowConfirmDialog(false);
    };

    // Manipulador para voltar à página anterior
    const handleBack = () => {
        navigate("/contratos");
    };

    // Manipulador para apagar o contrato (apenas no modo de edição)
    const handleDeleteRegister = async () => {
        try {
            await api.delete(`/contratos/${contratoId}`);
            setShowConfirmDeleteRegister(false);
            navigate("/contratos");
        } catch (error) {
            console.error("Erro ao remover contrato:", error);
            alert("Erro ao remover contrato.");
        }
    };

    const handleShowPdf = (url) => {
        setSelectedPdf(url);
        setShowDialog(true);
    };

    const handleCloseDialog = () => {
        setShowDialog(false);
    };

    return (
        <Template>
            <form onSubmit={handleSubmit} className="w-full px-4 py-6 flex flex-col gap-8">
                {/* Título da Página */}
                <div>
                    <h1 className="text-2xl text-stone-800 font-medium">
                        {isEditMode ? "Editar Contrato" : "Cadastrar Contrato"}
                    </h1>
                </div>

                {/* Informações Básicas */}
                <div className="flex flex-col gap-4">
                    <div className="text-stone-600 text-base font-medium">Informações Básicas </div>
                    <div className="grid grid-cols-1 sm:grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-4">
                        {/* Campo Contrato Nº */}
                        <div className="flex flex-col">
                            <FormLabel
                                htmlFor="numeroContrato"
                                isRequired={true}
                            >
                                Contrato Nº
                            </FormLabel>
                            <input
                                type="text"
                                name="numeroContrato"
                                value={formData.numeroContrato}
                                onChange={handleChange}
                                className="mt-1 w-full px-3 py-2 bg-white rounded-md border border-slate-300"
                                required
                            />
                        </div>

                        {/* Campo Data de Publicação */}
                        <div className="flex flex-col">
                            <FormLabel
                                htmlFor="dataPublicacao"
                                isRequired={true}
                            >Data de Publicação</FormLabel>
                            <input
                                type="date"
                                name="dataPublicacao"
                                value={formData.dataPublicacao}
                                onChange={handleChange}
                                className="mt-1 w-full px-3 py-2 bg-white rounded-md border border-slate-300"
                                required
                            />
                        </div>

                        {/* Campo Objeto do Contrato */}
                        <div className="flex flex-col md:col-span-2">
                            <label className="text-slate-700 text-sm font-medium">Objeto do Contrato</label>
                            <input
                                type="text"
                                name="objetoContrato"
                                value={formData.objetoContrato}
                                onChange={handleChange}
                                className="mt-1 w-full px-3 py-2 bg-white rounded-md border border-slate-300"

                            />
                        </div>
                    </div>

                    <div className="grid grid-cols-1 sm:grid-cols-2 md:grid-cols-4 lg:grid-cols-6 gap-4">
                        {/* Valor do Contrato */}
                        <div className="flex flex-col">
                            <label className="text-slate-700 text-sm font-medium">Valor do Contrato (R$)</label>
                            <input
                                type="text"
                                name="valorContrato"
                                value={formData.valorContrato}
                                onChange={handleCurrencyChange("valorContrato")}
                                className="mt-1 w-full px-3 py-2 bg-white rounded-md border border-slate-300"

                            />
                        </div>

                        {/* BDI (%) */}
                        <div className="flex flex-col">
                            <label className="text-slate-700 text-sm font-medium">BDI (%)</label>
                            <input
                                type="text"
                                name="bdiPercentual"
                                value={formData.bdiPercentual}
                                onChange={handleChange}
                                className="mt-1 w-full px-3 py-2 bg-white rounded-md border border-slate-300"

                            />
                        </div>

                        {/* Desconto (%) */}
                        <div className="flex flex-col">
                            <label className="text-slate-700 text-sm font-medium">Desconto (%)</label>
                            <input
                                type="text"
                                name="descontoPercentual"
                                value={formData.descontoPercentual}
                                onChange={handleInputChange}
                                className="mt-1 w-full px-3 py-2 bg-white rounded-md border border-slate-300"
                            />
                        </div>

                        {/* Processo Administrativo */}
                        <div className="flex flex-col">
                            <label className="text-slate-700 text-sm font-medium">Processo Administrativo</label>
                            <input
                                type="text"
                                name="processoAdministrativo"
                                value={formData.processoAdministrativo}
                                onChange={handleChange}
                                className="mt-1 w-full px-3 py-2 bg-white rounded-md border border-slate-300"
                            />
                        </div>


                    </div>
                </div>

                {/* Prazos e Datas */}
                <div className="flex flex-col gap-4">
                    <div className="text-stone-600 text-base font-medium">Prazos e Datas</div>
                    <div className="grid grid-cols-1 sm:grid-cols-2 md:grid-cols-4 gap-4">
                        {/* Prazo de Execução */}
                        <div className="flex flex-col">
                            <label className="text-slate-700 text-sm font-medium">Prazo de Execução</label>
                            <div className="mt-1 px-3 py-2.5 bg-white rounded-md border border-slate-300 inline-flex items-center gap-2.5">
                                <select
                                    name="prazoExecucao"
                                    value={formData.prazoExecucao}
                                    onChange={handleInputChange}
                                    className="grow bg-transparent outline-none text-slate-700 text-base"
                                >
                                    <option value="">Selecionar</option>
                                    {prazos.map((objeto) => (
                                        <option key={objeto.id} value={objeto.id}>
                                            {objeto.nome}
                                        </option>
                                    ))}
                                </select>
                            </div>
                        </div>

                        {/* Data de Início */}
                        <div className="flex flex-col">
                            <label className="text-slate-700 text-sm font-medium">Data de Início</label>
                            <input
                                type="date"
                                name="dataInicio"
                                value={formData.dataInicio}
                                onChange={handleChange}
                                className="mt-1 w-full px-3 py-2 bg-white rounded-md border border-slate-300"

                            />
                        </div>

                        {/* Data de Término */}
                        <div className="flex flex-col">
                            <label className="text-slate-700 text-sm font-medium">Data de Término</label>
                            <input
                                type="date"
                                name="dataTermino"
                                value={formData.dataTermino}
                                onChange={handleChange}
                                className="mt-1 w-full px-3 py-2 bg-white rounded-md border border-slate-300"

                            />
                        </div>

                    </div>
                </div>

                {/* ART */}
                <div className="flex flex-col gap-4">
                    <div className="text-stone-600 text-base font-medium">ART</div>
                    <div className="grid grid-cols-1 sm:grid-cols-2 md:grid-cols-3 gap-4">
                        {/* Nº da ART */}
                        <div className="flex flex-col">
                            <label className="text-slate-700 text-sm font-medium">Nº da ART</label>
                            <input
                                type="text"
                                name="numeroART"
                                value={formData.numeroART}
                                onChange={handleChange}
                                className="mt-1 w-full px-3 py-2 bg-white rounded-md border border-slate-300"

                            />
                        </div>

                        {/* Responsável pela ART */}
                        <div className="flex flex-col col-span-2">
                            <label className="text-slate-700 text-sm font-medium">Responsável pela ART</label>
                            <input
                                type="text"
                                name="responsavelART"
                                value={formData.responsavelART}
                                onChange={handleChange}
                                className="mt-1 w-full px-3 py-2 bg-white rounded-md border border-slate-300"

                            />
                        </div>
                    </div>
                </div>

                {/* Medições */}
                <div className="flex flex-col gap-4">
                    {/* Acumulado e Saldo */}
                    <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                        <div className="flex flex-col">
                            <label className="text-slate-800 font-bold">Acumulado (R$)</label>
                            <input
                                type="text"
                                name="acumulado"
                                value={formData.acumulado}
                                onChange={handleChange}
                                className="mt-1 w-full px-3 py-2 bg-white rounded-md border border-slate-300"
                                step="0.01"
                                readOnly
                            />
                        </div>
                        <div className="flex flex-col">
                            <label className="text-slate-800 font-bold">Saldo a Medir (R$)</label>
                            <input
                                type="text"
                                name="saldoMedir"
                                value={formData.saldoMedir}
                                onChange={handleCurrencyChange('saldoMedir')}
                                className="mt-1 w-full px-3 py-2 bg-white rounded-md border border-slate-300"
                                readOnly
                                step="0.01"
                            />
                        </div>
                    </div>
                </div>

                {/* Anexos e Documentos Complementares */}
                <div className="flex flex-col gap-4">
                    <div className="text-stone-600 text-base font-medium font-['Inter']">
                        Anexos e Documentos Complementares
                    </div>
                    {/* Linha de Envio de Documento */}
                    <div className="flex flex-col md:flex-row flex-wrap justify-start items-start gap-4">
                        <div className="md:w-[375.75px] w-full flex-col justify-center gap-1.5 inline-flex">
                            <label className="text-slate-700 text-sm font-normal font-['Inter'] leading-tight">
                                Tipo de Documento
                            </label>
                            <div className="px-3 py-2 bg-white rounded-md border border-slate-300 inline-flex items-center gap-2.5">
                                <select
                                    name="tipoDocumentoAnexo"
                                    value={formData.tipoDocumentoAnexo}
                                    onChange={handleInputChange}
                                    className="grow bg-transparent outline-none text-slate-700 text-base"

                                >
                                    <option value="">Selecionar</option>
                                    {tiposDocumentos.map((objeto) => (
                                        <option key={objeto.id} value={objeto.id}>
                                            {objeto.nome}
                                        </option>
                                    ))}
                                </select>
                                <div className="w-4 h-4 px-1 justify-center items-center flex overflow-hidden" />
                            </div>
                        </div>
                        <div className="grow flex-col gap-1.5 inline-flex">
                            <label className="text-slate-700 text-sm font-normal font-['Inter'] leading-tight">
                                Selecionar Documento
                            </label>
                            <div className="flex gap-2">
                                <div className="relative flex items-center gap-4 w-full">
                                    <input
                                        name="arquivo"
                                        type="file"
                                        accept=".pdf"
                                        onChange={handleAnexoFileChange}
                                        className="absolute inset-0 opacity-0 cursor-pointer grow pl-3 pr-14 py-2 bg-white rounded-md border border-slate-300 text-slate-400 text-base"


                                    />
                                    <div className={`w-full pl-3 pr-14 py-2 bg-white rounded-md border border-slate-300 flex items-center gap-2`} >
                                        <span className="text-base text-slate-400">
                                            {anexoFile ? anexoFile.name : "Pesquisar Documento"}
                                        </span>
                                    </div>
                                </div>
                                <button type="button" className="px-4 py-2 bg-stone-600 rounded-md flex justify-center items-center gap-2.5" onClick={handleAddAnexo}>
                                    <div className="text-white text-sm font-normal font-['Inter'] leading-normal">
                                        Enviar
                                    </div>
                                </button>
                            </div>
                        </div>
                    </div>
                    {/* Tabela de Anexos (conteúdo fixo) */}
                    <div className="w-full overflow-auto">
                        <table className="w-full text-left border-collapse">
                            <thead>
                                <tr>
                                    <th className="px-4 py-2 bg-stone-200 border border-slate-200 text-slate-800 text-base font-bold">
                                        Nº
                                    </th>
                                    <th className="px-4 py-2 bg-stone-200 border border-slate-200 text-slate-800 text-base font-bold">
                                        Tipo
                                    </th>
                                    <th className="px-4 py-2 bg-stone-200 border border-slate-200 text-slate-800 text-base font-bold">
                                        Data do Envio
                                    </th>
                                    <th className="px-4 py-2 bg-stone-200 border border-slate-200 text-slate-800 text-base font-bold">
                                        Remover
                                    </th>
                                </tr>
                            </thead>
                            <tbody>
                                {anexosSelecionados.length > 0 ? (
                                    anexosSelecionados.map((anexo) => (
                                        <tr key={anexo.id}>
                                            <td className="px-4 py-2 bg-white border border-slate-200 text-slate-700 text-base">{anexo.id}</td>
                                            <td className="px-4 py-2 bg-white border border-slate-200 text-slate-700 text-base">{anexo.tipoNome ?? anexo.tipo.nome}</td>
                                            <td className="px-4 py-2 bg-white border border-slate-200 text-slate-700 text-base">{formatDateWithoutHour(anexo.dataEnvio)}</td>
                                            <td className="px-4 py-2 bg-white border border-slate-200 text-center">
                                                {(isEditMode && !anexo.isNew && anexo.arquivo) && (
                                                    <button type="button" onClick={() => handleShowPdf(anexo.arquivo)} title="Ver anexo">
                                                        <img className="w-10 h-10 px-[3px] py-0.5 flex-col justify-center items-center inline-flex overflow-hidden" src="/images/icon/search.png" />
                                                    </button>
                                                )}
                                                <button type="button" onClick={() => handleRemoveAnexo(anexo.id)} title="Remover anexo">
                                                    <img className="w-6 h-6 px-[3px] py-0.5 flex-col justify-center items-center inline-flex overflow-hidden" src="/images/icon/trash.png" />
                                                </button>

                                            </td>
                                        </tr>
                                    ))
                                ) : (
                                    <tr>
                                        <td
                                            colSpan={5}
                                            className="px-4 py-2 bg-white border border-slate-200 text-slate-700 text-base text-center"
                                        >                                                Nenhum anexo enviado.
                                        </td>
                                    </tr>
                                )}
                            </tbody>
                        </table>
                    </div>

                    {/* Status e Observações / Atualizações */}
                    <div className="flex flex-col md:flex-row gap-8 w-full">
                        {/* Coluna 1 */}
                        <div className="md:w-1/2 w-full flex flex-col gap-4">
                            <div className="flex flex-col gap-4">
                                {/* Status / Classificação */}
                                <div className="flex flex-col md:flex-row flex-wrap gap-4">
                                    <div className="md:flex-1 w-full flex-col justify-center gap-1.5">
                                        <FormLabel htmlFor={"statusId"} isRequired={true}>
                                            Status
                                        </FormLabel>
                                        <div className="px-3 py-2 bg-white rounded-md border border-slate-300 inline-flex items-center gap-2.5">
                                            <select
                                                name="statusId"
                                                value={formData.statusId}
                                                onChange={handleInputChange}
                                                className="grow bg-transparent outline-none text-slate-700 text-base"
                                                required
                                            >
                                                <option value="">Selecionar</option>
                                                {statusList.map((objeto) => (
                                                    <option key={objeto.id} value={objeto.id}>
                                                        {objeto.nome}
                                                    </option>
                                                ))}
                                            </select>
                                            <div className="w-4 h-4 px-1 justify-center items-center flex overflow-hidden" />
                                        </div>
                                    </div>
                                </div>
                            </div>
                        </div>
                    </div>
                </div>

                {/* Botões de Ação */}

                <div className="flex items-center w-full gap-4">
                    {isEditMode && podeDeletar && (
                        <button
                            type="button"
                            className="px-4 py-2 bg-red-600 text-white text-sm font-medium rounded-md flex items-center gap-2"
                            onClick={() => setShowConfirmDeleteRegister(true)}
                        >
                            <img className="w-4 h-4" src="/images/icon/trash-2.png" alt="Apagar" />
                            <span>Apagar</span>
                        </button>
                    )}

                    <div className="flex gap-4 ml-auto">
                        <button
                            type="button"
                            className="px-4 py-2 bg-stone-600 text-white text-sm font-medium rounded-md flex items-center gap-2"
                            onClick={handleBack}
                        >
                            <img className="w-4 h-4" src="/images/icon/arrow-left.png" alt="Voltar" />
                            <span>Voltar</span>
                        </button>

                        <button
                            type="submit"
                            className="px-4 py-2 bg-stone-600 text-white text-sm font-medium rounded-md flex items-center gap-2"
                        >
                            <img className="w-4 h-4" src="/images/icon/save.png" alt="Salvar" />
                            <span>Salvar</span>
                        </button>
                    </div>
                </div>
            </form>

            {/* Modais de Confirmação e Sucesso */}
            <ConfirmationModal
                isVisible={showConfirmDialog}
                onConfirm={handleConfirmSubmit}
                onCancel={handleCancelSubmit}
                message="Deseja salvar as informações?"
            />

            <SuccessModal
                isVisible={showSuccessDialog}
                message="Informações salvas com sucesso!"
                onClose={() => setShowSuccessDialog(false)}
            />

            <ConfirmationModal
                isVisible={showConfirmDeleteRegister}
                onConfirm={handleDeleteRegister}
                onCancel={() => setShowConfirmDeleteRegister(false)}
                message="Deseja apagar este registro?"
                textButtonConfirm="Sim"
            />

            <ConfirmationModal
                isVisible={showConfirmRemoveArchiveDialog}
                onConfirm={() => { /* Implementar função para remover arquivo */ }}
                onCancel={() => setShowConfirmRemoveArchiveDialog(false)}
                message="Deseja remover o arquivo deste contrato?"
                textButtonConfirm="Sim"
            />

            {showDialog && (
                <div className="fixed top-0 left-0 w-full h-full bg-black/90 z-50 p-10 flex-col justify-start items-end gap-10 inline-flex overflow-hidden">
                    <div className=" rounded-lg w-full max-w-[80%] h-full max-h-[90%] flex flex-col">
                        <div className="flex justify-between items-center p-4">
                            <div className="flex items-center gap-4">
                                <a href={config.urlArquivo(selectedPdf)} download target="_blank">
                                    <img className="w-6 h-6 relative  overflow-hidden" src="/images/icon/download.png" />
                                </a>
                                <div className="w-6 h-6 relative  overflow-hidden" />
                                <div className="w-6 h-6 relative  overflow-hidden" />
                            </div>
                            <img className="w-6 h-6 relative  overflow-hidden cursor-pointer" src="/images/icon/x.png" onClick={handleCloseDialog} />
                        </div>
                        <div className="flex-grow flex justify-center items-center p-4 overflow-hidden">
                            <iframe
                                src={`${config.urlArquivo(selectedPdf)}#toolbar=0`}
                                className="w-full h-full border-0"
                                style={{ border: 'none' }}
                                title="PDF Viewer"
                            />
                        </div>
                    </div>
                </div>
            )}
        </Template>
    );
};

export default CadastrarContrato;
